import { useCallback, useRef } from 'react'

/**
 * Returns focus to whatever opened a dialog once it closes.
 *
 * Radix restores focus to a `Dialog.Trigger`, and these dialogs have none — they mount already open,
 * from a button that is no longer rendered by the time the dialog exists. Left alone, closing one
 * drops focus on `<body>` and a keyboard reader starts the page again from the top.
 *
 * The opener is read at open time rather than held as a ref, because the same dialog is opened from
 * several places — a row's own button, the selection bar, the page header. `fallbackRef` catches the
 * case where that control is gone when the dialog closes: a bulk remove takes its rows with it, and
 * the selection bar it was opened from goes with the selection.
 */
export const useReturnFocus = <Fallback extends HTMLElement = HTMLDivElement>() => {
  const opener = useRef<HTMLElement | null>(null)
  const fallbackRef = useRef<Fallback | null>(null)

  const captureOpener = useCallback(() => {
    const active = document.activeElement
    // `body` is what the document reports when nothing is focused — a control that takes no focus on
    // click, say. It is not somewhere to send a reader back to, so treat it as having no opener.
    opener.current = active instanceof HTMLElement && active !== document.body ? active : null
  }, [])

  const onCloseAutoFocus = useCallback((event: Event) => {
    // Radix would send focus to `body` here, so take it over whatever happens next.
    event.preventDefault()
    // Deferred past the commit that is unmounting the dialog: the control that opened it may be going
    // in the same commit — a bulk action closes its dialog and clears the selection the bar belongs to
    // — and `isConnected` read now still answers true for a button that is about to vanish.
    requestAnimationFrame(() => {
      const previous = opener.current
      const target = previous?.isConnected ? previous : fallbackRef.current
      target?.focus()
    })
  }, [])

  return { captureOpener, onCloseAutoFocus, fallbackRef }
}
